/**
 * Переключатели «кому открыто»: роли, команды, отдельные сотрудники.
 *
 * ── Почему переключатели, а не два списка ───────────────────────────────────
 *
 * Раньше доступ настраивался двумя списками подряд: «есть доступ» и «выдать
 * доступ», каждый со своей кнопкой. Читать это тяжело ровно потому, что одно и
 * то же состояние размазано по двум местам: чтобы понять, есть ли доступ у
 * человека, взгляд должен пройти оба списка и не найти его в первом.
 *
 * Переключатель отвечает на тот же вопрос одним взглядом: залит — доступ есть.
 *
 * ── Почему общий компонент ──────────────────────────────────────────────────
 *
 * Тем же самым настраиваются и разделы рабочего места, и папки быстрых
 * ответов. Две копии этого экрана разошлись бы — и разошлись бы незаметно:
 * пропавшая ветка выглядит как отсутствие адресатов, а не как поломка.
 *
 * Названия полей у двух API разные (folderId против teamId), поэтому наружу
 * отдаётся вид адресата, а сопоставление с полем делает вызывающий.
 */

import type { AccountFolderDto } from "../lib/api.js";
import { cn } from "@/lib/utils";

/** Выдача в приведённом виде: заполнено ровно одно из трёх. */
export interface AccessGrant {
  id: string;
  userId: string | null;
  role: string | null;
  teamId: string | null;
}

export type GrantSubject =
  | { kind: "user"; id: string }
  | { kind: "role"; id: string }
  | { kind: "team"; id: string };

interface AccessChipsProps {
  grants: readonly AccessGrant[];
  folders: readonly AccountFolderDto[];
  users: ReadonlyArray<{ id: string; displayName: string }>;
  busy: boolean;
  onGrant: (subject: GrantSubject) => void;
  onRevoke: (accessId: string) => void;
}

const ROLES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "MANAGER_INTERN", label: "Стажёр менеджера" },
  { value: "MANAGER", label: "Менеджер" },
  { value: "SENIOR_MANAGER", label: "Старший менеджер" },
  { value: "OPERATOR_INTERN", label: "Стажёр оператора" },
  { value: "OPERATOR", label: "Оператор" },
  { value: "SENIOR_OPERATOR", label: "Старший оператор" },
  { value: "HR", label: "HR" },
  { value: "ADMIN", label: "Администратор" },
];

const TRACKS: Record<string, string> = {
  MANAGER: "Менеджеры",
  OPERATOR: "Операторы",
  HR: "HR",
};

export function AccessChips({
  grants,
  folders,
  users,
  busy,
  onGrant,
  onRevoke,
}: AccessChipsProps) {
  /*
   * Выдачи разложены по адресатам заранее.
   *
   * Иначе каждый из полусотни переключателей искал бы себя перебором всего
   * списка при каждой перерисовке — а перерисовывается он на любое нажатие.
   */
  const byRole = new Map<string, AccessGrant>();
  const byTeam = new Map<string, AccessGrant>();
  const byUser = new Map<string, AccessGrant>();

  for (const grant of grants) {
    if (grant.role) byRole.set(grant.role, grant);
    else if (grant.teamId) byTeam.set(grant.teamId, grant);
    else if (grant.userId) byUser.set(grant.userId, grant);
  }

  const toggle = (
    existing: AccessGrant | undefined,
    subject: GrantSubject,
  ): void => {
    if (existing) onRevoke(existing.id);
    else onGrant(subject);
  };

  const roots = folders.filter((f) => f.parentId === null);

  return (
    <div className="flex flex-col gap-3">
      {/*
        Порядок групп не алфавитный: сверху то, чем пользуются постоянно
        («все операторы»), снизу — точечные исключения. Обратный порядок
        заставлял бы прокручивать сорок фамилий, чтобы добраться до строки,
        которая закрывает тот же вопрос целиком.
      */}
      <Group title="Роли" hint="Доступ получают все сотрудники с этой ролью">
        {ROLES.map((role) => (
          <Chip
            key={role.value}
            label={role.label}
            on={byRole.has(role.value)}
            busy={busy}
            onClick={() =>
              toggle(byRole.get(role.value), { kind: "role", id: role.value })
            }
          />
        ))}
      </Group>

      <Group
        title="Команды"
        hint="Доступ получает папка целиком, вместе с вложенными"
      >
        {roots.length === 0 && (
          <span className="text-xs text-muted-foreground">Папок пока нет</span>
        )}
        {roots.map((root) => (
          <span key={root.id} className="flex flex-wrap items-center gap-1">
            <Chip
              label={`${TRACKS[root.track] ?? root.track}: ${root.name}`}
              on={byTeam.has(root.id)}
              busy={busy}
              onClick={() =>
                toggle(byTeam.get(root.id), { kind: "team", id: root.id })
              }
            />
            {folders
              .filter((f) => f.parentId === root.id)
              .map((team) => (
                <Chip
                  key={team.id}
                  label={team.name}
                  nested
                  on={byTeam.has(team.id)}
                  busy={busy}
                  onClick={() =>
                    toggle(byTeam.get(team.id), { kind: "team", id: team.id })
                  }
                />
              ))}
          </span>
        ))}
      </Group>

      <Group
        title="Отдельные сотрудники"
        hint="Исключение, когда общее правило не подходит"
      >
        {users.length === 0 && (
          <span className="text-xs text-muted-foreground">
            Активных сотрудников нет
          </span>
        )}
        {users.map((user) => (
          <Chip
            key={user.id}
            label={user.displayName}
            on={byUser.has(user.id)}
            busy={busy}
            onClick={() =>
              toggle(byUser.get(user.id), { kind: "user", id: user.id })
            }
          />
        ))}
      </Group>
    </div>
  );
}

function Group({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {title}
        </span>
        <span className="text-[11px] text-muted-foreground/80">{hint}</span>
      </div>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function Chip({
  label,
  on,
  busy,
  nested,
  onClick,
}: {
  label: string;
  on: boolean;
  busy: boolean;
  nested?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors disabled:opacity-45",
        on
          ? "border-transparent bg-primary text-primary-foreground hover:bg-primary/90"
          : "border border-border bg-card text-muted-foreground hover:border-brand hover:text-brand",
        // Вложенная команда отступает: без сдвига «Ночная смена» читается как
        // самостоятельная папка, а не как часть ветки слева от неё.
        nested && "ml-0.5 text-[11px]",
      )}
    >
      {label}
    </button>
  );
}
